import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
    Building2,
    Calendar,
    DollarSign,
    EditIcon,
    FileText,
    Globe,
    Hash,
    Link2,
    Mail,
    MapPin,
    Phone,
    ReceiptText,
    Truck,
    User,
} from "lucide-react";
import LoadingAnimation from "@/v2/components/ui/LoadingAnimation";
import AccessControl from "@/v2/components/rbac/AccessControl";
import PurchaseOrderDeleteButton from "@/v2/components/ui/button/delete/PurchaseOrder";
import EditUploadPurchaseOrder from "@/v2/page/purchase-order/upload/Edit";
import ViewLayout from "@/v2/layout/View";
import { Badge } from "@/components/ui/badge";
import FindOnePurchaseOrder from "@/v2/api/purchase-order/FindOne";
import { PurchaseOrder, PurchaseOrderItem } from "@/v2/types";
import PrintPurchaseOrder from "@/v2/components/pages/purchase-order/Print";

const CDN_URL = import.meta.env.VITE_CDN_URL;

const NavButtons = ({ data }: { data: PurchaseOrder }) => {
    const type =
        data?.type === "MANPOWER_PURCHASE_ORDER"
            ? "manpower-purchase-order"
            : data?.type === "EQUIPMENT_RENTAL_PURCHASE_ORDER"
              ? "equipments-purchase-order"
              : "project-purchase-order";

    return (
        <>
            <AccessControl
                module="PURCHASE_ORDER_MANAGEMENT"
                requestedAccess={"PUT"}
            >
                {data?.uploaded ? (
                    <EditUploadPurchaseOrder
                        id={data.id}
                        type={data.type}
                        status={data?.status}
                        isInside
                    />
                ) : (
                    <Link
                        to={`/issued-purchase-order/${type}/edit/${data?.id}`}
                    >
                        <Button variant={"outline"} className="p-4" size={"sm"}>
                            <EditIcon className="h-4 w-4" />
                            {"Edit"}
                        </Button>
                    </Link>
                )}
            </AccessControl>
            <AccessControl
                module="PURCHASE_ORDER_MANAGEMENT"
                requestedAccess={"DELETE"}
            >
                <PurchaseOrderDeleteButton id={data?.id} isInside />
            </AccessControl>
            {!data?.uploaded && <PrintPurchaseOrder data={data} isInside />}
        </>
    );
};

export default function ViewOneEquipmentPurchaseOrder() {
    const { id: purchaseOrderId } = useParams();
    const navigate = useNavigate();

    if (!purchaseOrderId) navigate(-1);

    const query = useQuery({
        queryKey: ["EQUIPMENT_RENTAL_PURCHASE_ORDER", purchaseOrderId],
        queryFn: () => FindOnePurchaseOrder(purchaseOrderId!),
    });

    if (query.isLoading || query.isFetching) {
        return <LoadingAnimation />;
    }

    if (query.error) {
        return <p>Failed to Load Details...</p>;
    }

    if (!query.data) {
        return <p>No Data Found</p>;
    }

    const purchaseOrderData = query.data;

    const convertToTitleCase = (str: string) =>
        str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

    const formatDate = (dateString: string) => {
        // Drafts saved without a date come back as Go's zero time
        if (!dateString || dateString.startsWith("0001-01-01")) return "";
        return new Date(dateString).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
        });
    };

    return (
        <ViewLayout
            title="Equipment Purchase Order Details"
            description="Complete information about the equipment rental purchase order"
            icon={Truck}
            buttons={<NavButtons data={purchaseOrderData} />}
        >
            <CardContent className="space-y-8">
                {/* Badge for PO Type */}
                <div className="flex justify-start gap-2">
                    <Badge variant={"outline"} className="px-4 py-1 text-sm">
                        Equipment Rental Purchase Order
                    </Badge>
                    {purchaseOrderData?.status === "DRAFT" && (
                        <Badge
                            variant={"secondary"}
                            className="px-4 py-1 text-sm"
                        >
                            Draft
                        </Badge>
                    )}
                </div>

                {/* Basic Purchase Order Information */}
                <div>
                    <h3 className="mb-4 text-lg font-semibold">
                        Order Information
                    </h3>
                    <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                        <div className="space-y-2">
                            <div className="flex items-center text-sm text-muted-foreground">
                                <FileText className="mr-2 h-4 w-4" />
                                PO Number
                            </div>
                            <p className="text-base font-medium">
                                {purchaseOrderData?.purchase_order_number ||
                                    "N/A"}
                            </p>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center text-sm text-muted-foreground">
                                <Calendar className="mr-2 h-4 w-4" />
                                Issue Date
                            </div>
                            <p className="text-base font-medium">
                                {formatDate(purchaseOrderData?.order_date) ||
                                    "N/A"}
                            </p>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center text-sm text-muted-foreground">
                                <FileText className="mr-2 h-4 w-4" />
                                Reference Number
                            </div>
                            <p className="text-base font-medium">
                                {purchaseOrderData?.reference_number || "N/A"}
                            </p>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center text-sm text-muted-foreground">
                                <FileText className="mr-2 h-4 w-4" />
                                Project
                            </div>
                            <p className="text-base font-medium">
                                {purchaseOrderData?.project || "N/A"}
                            </p>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center text-sm text-muted-foreground">
                                <User className="mr-2 h-4 w-4" />
                                Recipient
                            </div>
                            <p className="text-base font-medium">
                                {purchaseOrderData?.recipient || "N/A"}
                            </p>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center text-sm text-muted-foreground">
                                <FileText className="mr-2 h-4 w-4" />
                                Recipient Designation
                            </div>
                            <p className="text-base font-medium">
                                {purchaseOrderData?.recipient_designation ||
                                    "N/A"}
                            </p>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center text-sm text-muted-foreground">
                                <FileText className="mr-2 h-4 w-4" />
                                Subject
                            </div>
                            <p className="text-base font-medium">
                                {purchaseOrderData?.subject || "N/A"}
                            </p>
                        </div>
                    </div>
                </div>

                <Separator />

                {/* Supplier Information */}
                <div>
                    <h3 className="mb-4 text-lg font-semibold">
                        Supplier Information
                    </h3>
                    <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                        <div className="space-y-2">
                            <div className="flex items-center text-sm text-muted-foreground">
                                <Building2 className="mr-2 h-4 w-4" />
                                Supplier Name
                            </div>
                            <p className="text-base font-medium">
                                {purchaseOrderData?.supplier?.full_name ||
                                    "N/A"}
                            </p>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center text-sm text-muted-foreground">
                                <Mail className="mr-2 h-4 w-4" />
                                Email
                            </div>
                            <p className="break-all text-base font-medium">
                                {purchaseOrderData?.supplier?.email || "N/A"}
                            </p>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center text-sm text-muted-foreground">
                                <Phone className="mr-2 h-4 w-4" />
                                Phone Number
                            </div>
                            <p className="text-base font-medium">
                                {purchaseOrderData?.supplier?.phone_number ||
                                    "N/A"}
                            </p>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center text-sm text-muted-foreground">
                                <Hash className="mr-2 h-4 w-4" />
                                CR Number
                            </div>
                            <p className="text-base font-medium">
                                {purchaseOrderData?.supplier?.cr_number ||
                                    "N/A"}
                            </p>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center text-sm text-muted-foreground">
                                <Hash className="mr-2 h-4 w-4" />
                                VAT Number
                            </div>
                            <p className="text-base font-medium">
                                {purchaseOrderData?.supplier?.vat_number ||
                                    "N/A"}
                            </p>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center text-sm text-muted-foreground">
                                <Globe className="mr-2 h-4 w-4" />
                                Website
                            </div>
                            <p className="break-all text-base font-medium">
                                {purchaseOrderData?.supplier?.website || "N/A"}
                            </p>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center text-sm text-muted-foreground">
                                <MapPin className="mr-2 h-4 w-4" />
                                Location
                            </div>
                            <p className="text-base font-medium">
                                {purchaseOrderData?.supplier?.locations?.[0] ||
                                    "N/A"}
                            </p>
                        </div>
                    </div>
                </div>

                <Separator />

                {/* Financial Summary */}
                <div>
                    <h3 className="mb-4 text-lg font-semibold">
                        Financial Summary
                    </h3>
                    <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                        <div className="space-y-2">
                            <div className="flex items-center text-sm text-muted-foreground">
                                <DollarSign className="mr-2 h-4 w-4" />
                                Total Amount
                            </div>
                            <p className="text-base font-medium">
                                {purchaseOrderData?.total_amount?.toFixed(2) ||
                                    "0.00"}{" "}
                                SR
                            </p>
                        </div>

                        {purchaseOrderData?.uploaded && (
                            <div className="space-y-2">
                                <div className="flex items-center text-sm text-muted-foreground">
                                    <ReceiptText className="mr-2 h-4 w-4" />
                                    Rate Type
                                </div>
                                <p className="text-base font-medium">
                                    {convertToTitleCase(
                                        purchaseOrderData?.rate_type,
                                    )}{" "}
                                    Rate
                                </p>
                            </div>
                        )}

                        {purchaseOrderData?.uploaded &&
                            purchaseOrderData?.purchase_order_file && (
                                <div className="space-y-2">
                                    <div className="flex items-center text-sm text-muted-foreground">
                                        <Link2 className="mr-2 h-4 w-4" />
                                        Attached Document
                                    </div>
                                    <a
                                        href={`${CDN_URL}/${purchaseOrderData?.purchase_order_file}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-base font-medium text-blue-500 hover:underline"
                                    >
                                        Download Document
                                    </a>
                                </div>
                            )}
                    </div>
                </div>

                {/* Items Table */}
                {!purchaseOrderData?.uploaded && purchaseOrderData?.items && (
                    <>
                        <Separator />
                        <div>
                            <h3 className="mb-4 text-lg font-semibold">
                                Order Items
                            </h3>
                            <div className="overflow-x-auto rounded-lg border">
                                <table className="w-full text-sm">
                                    <thead className="bg-muted">
                                        <tr>
                                            <th className="p-3 text-left font-medium">
                                                Sl.No
                                            </th>
                                            <th className="p-3 text-left font-medium">
                                                Description
                                            </th>
                                            <th className="p-3 text-left font-medium">
                                                Unit
                                            </th>
                                            <th className="p-3 text-right font-medium">
                                                Quantity
                                            </th>
                                            <th className="p-3 text-right font-medium">
                                                Unit Price
                                            </th>
                                            <th className="p-3 text-right font-medium">
                                                Tax
                                            </th>
                                            <th className="p-3 text-right font-medium">
                                                Net Amount
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y">
                                        {purchaseOrderData?.items?.map(
                                            (item: PurchaseOrderItem) => (
                                                <tr key={item.description}>
                                                    <td className="p-3">
                                                        {item.serial_number}
                                                    </td>
                                                    <td className="p-3">
                                                        {item.description}
                                                    </td>
                                                    <td className="p-3">
                                                        {item.unit}
                                                    </td>
                                                    <td className="p-3 text-right">
                                                        {item.quantity}
                                                    </td>
                                                    <td className="p-3 text-right">
                                                        {item.unit_price?.toFixed(
                                                            2,
                                                        )}{" "}
                                                        SR
                                                    </td>
                                                    <td className="p-3 text-right">
                                                        {item.tax_rate?.toFixed(
                                                            2,
                                                        )}{" "}
                                                        %
                                                    </td>
                                                    <td className="p-3 text-right">
                                                        {item.net_amount?.toFixed(
                                                            2,
                                                        )}{" "}
                                                        SR
                                                    </td>
                                                </tr>
                                            ),
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                )}

                {/* Terms and Conditions */}
                {!purchaseOrderData?.uploaded &&
                    purchaseOrderData?.terms_and_conditions && (
                        <>
                            <Separator />
                            <div>
                                <h3 className="mb-4 text-lg font-semibold">
                                    Terms and Conditions
                                </h3>
                                <div className="space-y-2 rounded-lg border bg-muted/50 p-4">
                                    {purchaseOrderData?.terms_and_conditions.map(
                                        (terms: string, index: number) => (
                                            <div
                                                key={index}
                                                className="flex items-start gap-2"
                                            >
                                                <span className="font-medium">
                                                    {index + 1}.
                                                </span>
                                                <p>{terms}</p>
                                            </div>
                                        ),
                                    )}
                                </div>
                            </div>
                        </>
                    )}

                {/* Remarks */}
                {purchaseOrderData?.remarks && (
                    <>
                        <Separator />
                        <div>
                            <h3 className="mb-4 text-lg font-semibold">
                                Remarks
                            </h3>
                            <div className="rounded-lg border bg-muted/50 p-4">
                                <p>{purchaseOrderData.remarks}</p>
                            </div>
                        </div>
                    </>
                )}
            </CardContent>
        </ViewLayout>
    );
}
